import type { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, Lightbulb, TrendingDown, type LucideIcon } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { cn } from '@/lib/utils';
import type { InsightLevel, ProductInsight } from '@/lib/product-intelligence';
import { vendorColor } from './vendor-colors';

/**
 * Small building blocks shared by the Intelligence tab's three panels. They
 * follow the product page's own idiom — white cards on a hairline border,
 * 11px uppercase slate-600 headings — so the tab reads as part of the page.
 */

/** 11px uppercase heading — the same treatment as the vendor table heads. */
export function SectionLabel({ children, trailing }: { children: ReactNode; trailing?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <h4 className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">{children}</h4>
      {trailing}
    </div>
  );
}

export function IntelCard({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('rounded-xl border border-border bg-white p-4', className)}>{children}</div>;
}

/** One figure and its caption. `alert` tints the figure the owner should act on. */
export function MetricTile({ value, caption, alert }: { value: string; caption: string; alert?: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-white px-3 py-3 text-center">
      <div className={cn('truncate text-lg font-semibold tabular-nums', alert ? 'text-amber-600' : 'text-foreground')}>
        {value}
      </div>
      <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">{caption}</div>
    </div>
  );
}

const INSIGHT_STYLE: Record<InsightLevel, { icon: LucideIcon; bar: string; tint: string; iconCls: string }> = {
  critical: { icon: AlertCircle, bar: 'bg-red-500', tint: 'bg-red-50', iconCls: 'text-red-600' },
  warning: { icon: AlertTriangle, bar: 'bg-amber-500', tint: 'bg-amber-50', iconCls: 'text-amber-600' },
  info: { icon: Lightbulb, bar: 'bg-primary', tint: 'bg-primary/5', iconCls: 'text-primary' },
  good: { icon: TrendingDown, bar: 'bg-green-600', tint: 'bg-green-50', iconCls: 'text-green-700' },
};

/** One generated sentence, coloured by severity. The icon carries the level
 *  too, so it never rests on colour alone. */
export function InsightStrip({ insight }: { insight: ProductInsight }) {
  const s = INSIGHT_STYLE[insight.level];
  const Icon = s.icon;
  return (
    <div className={cn('flex overflow-hidden rounded-lg', s.tint)} data-insight={insight.rule}>
      <span className={cn('w-[3px] shrink-0', s.bar)} aria-hidden="true" />
      <div className="flex flex-1 items-start gap-2.5 px-3 py-2.5">
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', s.iconCls)} aria-hidden="true" />
        <p className="text-sm text-foreground">{insight.message}</p>
      </div>
    </div>
  );
}

export function InsightList({ insights }: { insights: ProductInsight[] }) {
  if (insights.length === 0) return null;
  return (
    <div className="space-y-2" aria-label="Insights">
      {insights.map((i) => (
        <InsightStrip key={i.rule} insight={i} />
      ))}
    </div>
  );
}

/** Static grey bars at the shape of what's coming. No pulse: several load at
 *  once on this tab, and independently pulsing blocks read as jitter. */
export function SectionSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <IntelCard>
      <div className="space-y-2.5" aria-busy="true" aria-label="Loading">
        {Array.from({ length: lines }, (_, i) => (
          <div key={i} className={cn('h-3.5 rounded bg-muted', i % 2 === 1 ? 'w-1/2' : 'w-full')} />
        ))}
      </div>
    </IntelCard>
  );
}

/** Empty result inside a card. On this tab an empty result is usually a real
 *  answer (a price that held, a product never counted), so it says which. */
export function IntelEmpty({ icon, title, detail }: { icon: LucideIcon; title: string; detail?: ReactNode }) {
  return (
    <IntelCard className="p-0">
      <EmptyState icon={icon} title={title} description={detail} className="py-8" />
    </IntelCard>
  );
}

export function IntelError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <IntelCard>
      <div className="flex items-start gap-2.5">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
        <p className="flex-1 text-sm text-foreground">{message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="shrink-0 rounded-lg border border-border bg-white px-3 py-1 text-xs font-medium text-foreground hover:bg-muted"
          >
            Retry
          </button>
        )}
      </div>
    </IntelCard>
  );
}

/**
 * Signed percentage with an arrow. Colour is inverted against the usual
 * finance convention on purpose: this is a cost, so a rise is bad (red) and a
 * fall is good (green). The arrow carries the direction for anyone reading
 * the colour the other way round.
 */
export function DeltaLabel({ fraction, className }: { fraction: number | null; className?: string }) {
  if (fraction === null || Math.abs(fraction) < 0.0001) {
    return <span className={cn('text-xs font-medium text-muted-foreground', className)}>—</span>;
  }
  const up = fraction > 0;
  return (
    <span
      className={cn('text-xs font-semibold tabular-nums', up ? 'text-red-600' : 'text-green-700', className)}
      title={up ? 'Price went up' : 'Price went down'}
    >
      {up ? '▲' : '▼'}
      {(Math.abs(fraction) * 100).toFixed(1)}%
    </span>
  );
}

export function ColorDot({ index, className }: { index: number; className?: string }) {
  return (
    <span
      className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full', className)}
      style={{ background: vendorColor(index) }}
      aria-hidden="true"
    />
  );
}

/** A segmented control: one selected option out of a few. Buttons are
 *  type="button" because the whole product page is one <form>. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: readonly { value: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div role="group" aria-label={label} className={cn('inline-flex rounded-lg bg-muted p-0.5', className)}>
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm',
              selected ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
